<template>
  <KycDesktop
    :ratio="1/8"
    :next-button-theme="nextButtonTheme"
    :title="$t('kyc.titles.identity')"
    back-route="/opportunities"
    @next="next"
  >
    <div>
      <div class="text-xl font-serif mb-3">
        Vous
      </div>
      <div class="flex">
        <BaseInputGroup
          v-model="attributes.job"
          :label="$t('kyc.labels.job')"
          class="w-1/2 mb-8 mr-20"
          autofocus
        />
        <BaseSelect
          v-model="attributes.taxResidence"
          :label="$t('kyc.labels.taxResidence')"
          :options="countries"
          value-property="code"
          track-by="code"
          select-label="name"
          class="w-1/2 mb-8"
        />
      </div>
      <div class="text-xl font-serif">
        {{ $t('kyc.questions.maritalStatus') }}
      </div>
      <BaseRadioButtons
        :items="maritalStatusItems"
        :value="attributes.maritalStatus"
        class="mb-8"
        @input="updateMaritalStatus"
      >
        <BaseRadioButton
          slot-scope="slotProps"
          :active="slotProps.item.title === attributes.maritalStatus"
        >
          {{ slotProps.item.title }}
        </BaseRadioButton>
      </BaseRadioButtons>
      <template v-if="married">
        <BaseSmallTitle>
          {{ $t('kyc.questions.marriageType') }}
        </BaseSmallTitle>
        <BaseRadioButtons
          :items="marriageTypeItems"
          :value="attributes.marriageType"
          class="mb-8"
          @input="updateMarriageType"
        >
          <BaseRadioButton
            slot-scope="slotProps"
            :active="slotProps.item.title === attributes.marriageType"
          >
            {{ slotProps.item.title }}
          </BaseRadioButton>
        </BaseRadioButtons>
      </template>
      <template v-if="married || pacsed">
        <BaseSmallTitle class="mb-1">
          {{ $t('kyc.questions.spouse') }}
        </BaseSmallTitle>
        <div class="flex">
          <BaseSelect
            v-model="attributes.spouseCitizenship"
            :label="$t('kyc.labels.citizenship')"
            :options="countries"
            value-property="code"
            track-by="code"
            select-label="name"
            class="mr-10 flex-1"
          />
          <BaseSelect
            v-model="attributes.spouseTaxResidence"
            :label="$t('kyc.labels.taxResidence')"
            :options="countries"
            value-property="code"
            track-by="code"
            select-label="name"
            class="flex-1 mb-8"
          />
        </div>
      </template>
      <BaseSmallTitle>
        {{ $t('kyc.questions.children') }}
      </BaseSmallTitle>
      <BaseRadioButtons
        v-model="hasChildren"
        :items="hasChildrenItems"
        class="mb-8"
      >
        <BaseRadioButton
          slot-scope="slotProps"
          :active="slotProps.item === hasChildren"
        >
          {{ slotProps.item.title }}
        </BaseRadioButton>
      </BaseRadioButtons>
      <template v-if="hasChildren && hasChildren.id === 2">
        <BaseSmallTitle class="mb-3">
          {{ $t('kyc.questions.childrenDetails') }}
        </BaseSmallTitle>
        <BaseInputGroup
          v-for="(age, index) in attributes.childrenAges"
          v-model="age.value"
          :key="index"
          :label="$t('kyc.labels.childrenDetails', { index: (index + 1) })"
          :class="{'mb-4': (index < attributes.childrenAges.length - 1) }"
          type="number"
          min="0"
          max="100"
          small
          with-delete-button
          @remove="removeChild(index)"
        />
        <BaseAddLine
          class="text-xl mb-4"
          theme="orange"
          @click.native="addChild"
        >
          Ajouter une personne
        </BaseAddLine>
      </template>
      <BaseSmallTitle class="mb-1">
        {{ $t('kyc.questions.ppe') }}
      </BaseSmallTitle>
      <BaseParagraph>{{ $t('kyc.labels.ppe') }}</BaseParagraph>
      <BaseRadioButtons
        :value="attributes.ppe"
        :items="ppeItems"
        class="mb-4"
        @input="updatePpe"
      >
        <BaseRadioButton
          slot-scope="slotProps"
          :active="slotProps.item.value === attributes.ppe"
        >
          {{ slotProps.item.title }}
        </BaseRadioButton>
      </BaseRadioButtons>
    </div>
  </KycDesktop>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions } from 'vuex';
import { isNil } from 'lodash';
import {
  PPE_ITEMS,
  HAS_CHILDREN_ITEMS,
  MARITAL_STATUS_ITEMS,
  MARRIAGE_TYPE_ITEMS,
} from '../../../constants';
import BaseFormHeader from '../../../components/BaseFormHeader.vue';
import BaseSubtitle from '../../../components/BaseSubtitle.vue';
import BaseInputGroup from '../../../components/BaseInputGroup.vue';
import BaseRadioButtons from '../../../components/BaseRadioButtons.vue';
import BaseRadioButton from '../../../components/BaseRadioButton.vue';
import BaseSelect from '../../../components/BaseSelect.vue';
import BaseAddLine from '../../../components/BaseAddLine.vue';
import BaseSmallTitle from '../../../components/BaseSmallTitle.vue';
import BaseParagraph from '../../../components/BaseParagraph.vue';
import KycDesktop from '../Desktop.vue';
import kyc from '../../../mixins/kyc';
import countries from '../../../mixins/countries.json';

export default {
  name: 'Identity',
  components: {
    BaseFormHeader,
    BaseSubtitle,
    KycDesktop,
    BaseInputGroup,
    BaseRadioButton,
    BaseRadioButtons,
    BaseSelect,
    BaseAddLine,
    BaseSmallTitle,
    BaseParagraph,
  },
  mixins: [kyc],
  data() {
    return {
      maritalStatusItems: MARITAL_STATUS_ITEMS,
      marriageTypeItems: MARRIAGE_TYPE_ITEMS,
      hasChildrenItems: HAS_CHILDREN_ITEMS,
      ppeItems: PPE_ITEMS,
      countries,
      hasChildren: null,
    };
  },
  computed: {
    ...mapFields('kyc', ['form.attributes']),
    married() {
      return (this.attributes.maritalStatus === 'Marié(e)');
    },
    pacsed() {
      return (this.attributes.maritalStatus === 'Pacsé(e)');
    },
    nextButtonTheme() {
      let theme = 'primary';
      ['job', 'maritalStatus'].forEach((key) => {
        if (!this.attributes[key]) {
          theme = 'disabled';
        }
      });
      if (isNil(this.attributes.ppe)) {
        theme = 'disabled';
      }
      if (!this.hasChildren) {
        return 'disabled';
      }
      if (this.married && !this.attributes.marriageType) {
        return 'disabled';
      }
      if ((this.married || this.pacsed)
          && !(this.attributes.spouseTaxResidence && this.attributes.spouseCitizenship)) {
        return 'disabled';
      }
      if (this.hasChildren && this.hasChildren.id === 2) {
        if (!this.attributes.childrenAges.length || !this.attributes.childrenAges[0].value) {
          return 'disabled';
        }
      }
      return theme;
    },
  },
  created() {
    if (this.attributes.childrenAges && this.attributes.childrenAges.length) {
      [, this.hasChildren] = HAS_CHILDREN_ITEMS;
    }
  },
  methods: {
    ...mapActions('kyc', ['update']),
    updateMaritalStatus(input) {
      this.attributes.maritalStatus = input.title;
    },
    updateMarriageType(input) {
      this.attributes.marriageType = input.title;
    },
    updatePpe(input) {
      this.attributes.ppe = input.value;
    },
    addChild() {
      this.attributes.childrenAges.push({ value: null });
    },
    removeChild(index) {
      this.attributes.childrenAges.splice(index, 1);
    },
    next() {
      this.update();
      this.$router.push('/kyc/desktop/financial/introduction');
    },
  },
};
</script>
